/**
 * Two of a school classroom card's panels: who is in the class, and its books.
 *
 * School console only — CWY's own workspace has no roster to show and no shelf
 * to stock — so `ClassroomsManager` renders these behind its `classPanels` prop.
 * Both read `/tenant-admin`, which CWY support reaches for a managed school
 * through the same audited flow as every other screen here.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MAX_BOOK_BYTES, PDF_CONTENT_TYPE } from '@cwy/config/contracts';
import type { ClassBookDTO, ClassroomDTO, ClassroomStudentDTO } from '@cwy/config/contracts';
import { apiDelete, apiErrorMessage, apiGet, apiPost, uploadToS3 } from '@/lib/api';
import { formatBookSize, openBook } from '@/lib/books';
import { queryClient } from '@/lib/queryClient';
import { Button, Field, Input, ProgressBar } from '@/components/ds';

const MAX_BOOK_MB = Math.floor(MAX_BOOK_BYTES / 1024 / 1024);

/** Everyone enrolled in this class now, in register order. */
export function ClassStudentsPanel({ classroom }: { classroom: ClassroomDTO }) {
  const students = useQuery({
    queryKey: ['tenant-admin', 'classrooms', classroom.id, 'students'],
    queryFn: () =>
      apiGet<ClassroomStudentDTO[]>(`/tenant-admin/classrooms/${classroom.id}/students`),
  });

  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-line pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-bold text-ink">Students in {classroom.name}</h4>
        {/* Relative to this console's own Classrooms route, so the same link
            works in a school's console and under Schools → Manage. */}
        <Link
          to={`../students?class=${encodeURIComponent(classroom.id)}`}
          relative="path"
          className="text-xs font-semibold no-underline"
          style={{ color: 'var(--text-accent)' }}
        >
          Manage in Students
        </Link>
      </div>
      {students.isPending && <p className="text-sm text-ink-soft">Loading…</p>}
      {students.error && <p className="text-sm text-danger">{apiErrorMessage(students.error)}</p>}
      {students.data?.length === 0 && (
        <p className="text-sm text-ink-soft">
          Nobody is enrolled yet. Add students under Students, naming this class’s code.
        </p>
      )}
      {(students.data?.length ?? 0) > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                <th className="py-1.5 pr-3 text-left font-semibold">Roll</th>
                <th className="py-1.5 pr-3 text-left font-semibold">Name</th>
                <th className="py-1.5 text-left font-semibold">Sign-in ID</th>
              </tr>
            </thead>
            <tbody>
              {students.data?.map((student) => (
                <tr
                  key={student.membershipId}
                  style={{ borderBottom: '1px solid var(--border-subtle)' }}
                >
                  <td className="py-1.5 pr-3" data-numeric="">
                    {student.rollNumber ?? '—'}
                  </td>
                  <td className="py-1.5 pr-3">
                    {student.name}
                    {student.status !== 'ACTIVE' && (
                      <span className="ml-1.5 text-xs text-ink-faint">
                        · {student.status === 'SUSPENDED' ? 'suspended' : 'left'}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-ink-soft" data-numeric="">
                    {student.loginId ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The title a file suggests: its name without the extension, separators as spaces. */
function titleFromFile(name: string): string {
  return name
    .replace(/\.pdf$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .slice(0, 200);
}

/**
 * The class's books: upload a PDF, list what is on the shelf, remove one.
 *
 * The file goes straight to storage through a presigned PUT; the API is told
 * only the key it issued, and checks it belongs to this class before the book
 * reaches a single student.
 */
export function ClassBooksPanel({ classroom }: { classroom: ClassroomDTO }) {
  const queryKey = ['tenant-admin', 'classrooms', classroom.id, 'books'];
  const books = useQuery({
    queryKey,
    queryFn: () => apiGet<ClassBookDTO[]>(`/tenant-admin/classrooms/${classroom.id}/books`),
  });

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [inputKey, setInputKey] = useState(0);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a PDF first.');
      const { uploadUrl, s3Key } = await apiPost<{ uploadUrl: string; s3Key: string }>(
        `/tenant-admin/classrooms/${classroom.id}/books/presign`,
        { fileName: file.name, contentType: PDF_CONTENT_TYPE, sizeBytes: file.size },
      );
      setProgress(0);
      await uploadToS3(uploadUrl, file, PDF_CONTENT_TYPE, setProgress);
      return apiPost<ClassBookDTO>(`/tenant-admin/classrooms/${classroom.id}/books`, {
        title: title.trim() || titleFromFile(file.name),
        s3Key,
        fileName: file.name,
        sizeBytes: file.size,
      });
    },
    onSuccess: () => {
      setFile(null);
      setTitle('');
      setProgress(null);
      // A fresh input, so choosing the same file again still fires onChange.
      setInputKey((k) => k + 1);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setProgress(null);
      setError(apiErrorMessage(err));
    },
  });

  const remove = useMutation({
    mutationFn: (book: ClassBookDTO) => apiDelete(`/tenant-admin/books/${book.id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!file) return setError('Choose a PDF first.');
    upload.mutate();
  };

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
      <div>
        <h4 className="text-sm font-bold text-ink">Books for {classroom.name}</h4>
        <p className="text-xs text-ink-soft">
          Every student in this class sees these under Learn → Class books. PDF only, up to{' '}
          {MAX_BOOK_MB} MB each.
        </p>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {books.isPending && <p className="text-sm text-ink-soft">Loading…</p>}
      {books.error && <p className="text-sm text-danger">{apiErrorMessage(books.error)}</p>}
      {books.data?.length === 0 && (
        <p className="text-sm text-ink-soft">No books on this class’s shelf yet.</p>
      )}
      {(books.data?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-1.5">
          {books.data?.map((book) => (
            <li
              key={book.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-3 py-2"
            >
              <span className="min-w-0 text-fs-13">
                <strong className="font-semibold text-forest">{book.title}</strong>
                <span className="text-ink-soft">
                  {' '}
                  · {formatBookSize(book.sizeBytes)} · added{' '}
                  {new Date(book.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  {book.uploadedBy ? ` by ${book.uploadedBy}` : ' by CWY'}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  className="text-xs font-semibold"
                  style={{ color: 'var(--text-accent)' }}
                  onClick={() => {
                    setError('');
                    openBook(`/tenant-admin/books/${book.id}/url`).catch((err) =>
                      setError(apiErrorMessage(err)),
                    );
                  }}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    setError('');
                    if (
                      window.confirm(
                        `Remove “${book.title}” from ${classroom.name}? Students stop seeing it at once.`,
                      )
                    ) {
                      remove.mutate(book);
                    }
                  }}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <form className="flex flex-wrap items-end gap-2.5" onSubmit={submit} noValidate>
        <label className="cursor-pointer">
          <span className="inline-flex rounded-[var(--control-radius)] border border-line px-3 py-1.5 text-sm font-semibold text-ink">
            {file ? 'Choose another PDF' : 'Choose a PDF'}
          </span>
          <input
            key={inputKey}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            disabled={upload.isPending}
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setError('');
              if (!chosen) return;
              if (chosen.type && chosen.type !== PDF_CONTENT_TYPE) {
                setError('Books are PDF files.');
                return;
              }
              if (chosen.size > MAX_BOOK_BYTES) {
                setError(
                  `That file is ${Math.ceil(chosen.size / 1024 / 1024)} MB — a book can be at most ${MAX_BOOK_MB} MB.`,
                );
                return;
              }
              setFile(chosen);
              setTitle(titleFromFile(chosen.name));
            }}
          />
        </label>
        {file && (
          <>
            <Field label="Title" className="min-w-[220px] flex-1">
              {({ id }) => (
                <Input
                  id={id}
                  value={title}
                  maxLength={200}
                  disabled={upload.isPending}
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" disabled={upload.isPending || !title.trim()}>
              {upload.isPending
                ? progress !== null && progress < 100
                  ? `Uploading… ${progress}%`
                  : 'Saving…'
                : 'Add book'}
            </Button>
          </>
        )}
      </form>
      {file && (
        <p className="text-xs text-ink-soft">
          {file.name} · {formatBookSize(file.size)}
        </p>
      )}
      {progress !== null && <ProgressBar value={progress} height={6} />}
    </div>
  );
}
